import type { MediaRef } from "@aurumiq/cms-schema";
import { Button } from "@aurumiq/ui/components/button";
import { Input } from "@aurumiq/ui/components/input";
import { Label } from "@aurumiq/ui/components/label";
import { Switch } from "@aurumiq/ui/components/switch";
import { Textarea } from "@aurumiq/ui/components/textarea";
import { cn } from "@aurumiq/ui/lib/utils";
import { ArrowDown, ArrowUp, ImageIcon, Plus, Trash2, X } from "lucide-react";
import { useId, useState } from "react";
import type { z } from "zod";
import { NativeSelect } from "@/components/field";
import { MediaPickerDialog } from "../media-picker";
import { emptyValue, type FieldSpec, labelOf } from "./introspect";

type Issue = Pick<z.core.$ZodIssue, "path" | "message">;

export interface SchemaFormProps {
  fields: Record<string, FieldSpec>;
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  issues?: readonly Issue[];
  /** Keys not rendered (e.g. `id`, `type`). */
  omit?: readonly string[];
  /** Path prefix used to match issues for nested forms. */
  basePath?: (string | number)[];
  disabled?: boolean;
}

function issueFor(issues: readonly Issue[] | undefined, path: (string | number)[]) {
  const key = path.join(".");
  return issues?.find((i) => i.path.join(".") === key)?.message;
}

function hasIssueUnder(issues: readonly Issue[] | undefined, path: (string | number)[]) {
  const key = `${path.join(".")}.`;
  return issues?.some((i) => i.path.join(".").startsWith(key)) ?? false;
}

/**
 * Renders an object form from introspected zod field specs. Values are kept as the raw JSON
 * that the schema will parse (strings stay strings, numbers stay numbers, `undefined` = unset).
 */
export function SchemaForm({
  fields,
  value,
  onChange,
  issues,
  omit = [],
  basePath = [],
  disabled,
}: SchemaFormProps) {
  const set = (key: string, v: unknown) => {
    const next = { ...value };
    if (v === undefined) delete next[key];
    else next[key] = v;
    onChange(next);
  };
  return (
    <div className="grid gap-3">
      {Object.entries(fields)
        .filter(([k, f]) => !omit.includes(k) && f.kind !== "literal")
        .map(([key, spec]) => (
          <FieldControl
            key={key}
            label={labelOf(key)}
            spec={spec}
            value={value[key]}
            onChange={(v) => set(key, v)}
            path={[...basePath, key]}
            issues={issues}
            disabled={disabled}
          />
        ))}
    </div>
  );
}

interface ControlProps {
  label: string;
  spec: FieldSpec;
  value: unknown;
  onChange: (v: unknown) => void;
  path: (string | number)[];
  issues?: readonly Issue[];
  disabled?: boolean;
}

function Wrap({
  id,
  label,
  error,
  hint,
  children,
  inline,
}: {
  id?: string;
  label: string;
  error?: string;
  hint?: string;
  children: React.ReactNode;
  inline?: boolean;
}) {
  return (
    <div className={cn("min-w-0", inline && "flex items-center justify-between gap-3")}>
      <Label htmlFor={id}>{label}</Label>
      {children}
      {error ? (
        <p className="mt-1 w-full text-destructive text-xs">{error}</p>
      ) : hint ? (
        <p className="mt-1 text-muted-foreground text-xs">{hint}</p>
      ) : null}
    </div>
  );
}

export function FieldControl({
  label,
  spec,
  value,
  onChange,
  path,
  issues,
  disabled,
}: ControlProps) {
  const id = useId();
  const error = issueFor(issues, path);
  const str = (v: unknown) => (v === undefined || v === null ? "" : String(v));
  const setStr = (s: string) => onChange(spec.optional && s === "" ? undefined : s);

  switch (spec.kind) {
    case "string":
      return (
        <Wrap id={id} label={label} error={error}>
          <Input
            id={id}
            type={spec.format === "url" ? "url" : spec.format === "email" ? "email" : "text"}
            value={str(value)}
            maxLength={spec.max}
            onChange={(e) => setStr(e.target.value)}
            aria-invalid={error ? true : undefined}
            disabled={disabled}
          />
        </Wrap>
      );
    case "text":
      return (
        <Wrap
          id={id}
          label={label}
          error={error}
          hint={spec.max ? `Up to ${spec.max} characters` : undefined}
        >
          <Textarea
            id={id}
            rows={4}
            value={str(value)}
            maxLength={spec.max}
            onChange={(e) => setStr(e.target.value)}
            aria-invalid={error ? true : undefined}
            disabled={disabled}
          />
        </Wrap>
      );
    case "number":
      return (
        <Wrap
          id={id}
          label={label}
          error={error}
          hint={spec.nullable ? "Leave blank for “none”" : undefined}
        >
          <Input
            id={id}
            type="number"
            inputMode={spec.int ? "numeric" : "decimal"}
            step={spec.int ? 1 : "any"}
            min={spec.min}
            max={spec.max}
            value={str(value)}
            onChange={(e) => {
              const s = e.target.value;
              if (s === "") onChange(spec.nullable ? null : undefined);
              else onChange(spec.int ? Number.parseInt(s, 10) : Number(s));
            }}
            aria-invalid={error ? true : undefined}
            disabled={disabled}
          />
        </Wrap>
      );
    case "boolean":
      return (
        <Wrap id={id} label={label} error={error} inline>
          <Switch
            id={id}
            checked={Boolean(value ?? spec.default ?? false)}
            onCheckedChange={(c) => onChange(c)}
            disabled={disabled}
          />
        </Wrap>
      );
    case "enum":
      return (
        <Wrap id={id} label={label} error={error}>
          <NativeSelect
            id={id}
            value={str(value ?? spec.default)}
            onChange={(e) => {
              const s = e.target.value;
              if (s === "") return onChange(undefined);
              const opt = spec.options.find((o) => String(o) === s);
              onChange(opt);
            }}
            aria-invalid={error ? true : undefined}
            disabled={disabled}
          >
            {spec.optional && spec.default === undefined ? <option value="">—</option> : null}
            {spec.options.map((o) => (
              <option key={String(o)} value={String(o)}>
                {String(o)}
              </option>
            ))}
          </NativeSelect>
        </Wrap>
      );
    case "media":
      return (
        <MediaField
          id={id}
          label={label}
          value={value as MediaRef | undefined}
          onChange={onChange}
          error={error ?? (hasIssueUnder(issues, path) ? "Invalid image" : undefined)}
          disabled={disabled}
        />
      );
    case "array":
      return (
        <ArrayField
          label={label}
          spec={spec}
          value={Array.isArray(value) ? value : []}
          onChange={onChange}
          path={path}
          issues={issues}
          disabled={disabled}
        />
      );
    case "object": {
      const obj = (value ?? {}) as Record<string, unknown>;
      const present = value !== undefined;
      return (
        <fieldset className="rounded-lg border p-3">
          <legend className="flex items-center gap-2 px-1 font-medium text-sm">
            {label}
            {spec.optional ? (
              <Switch
                aria-label={`${present ? "Remove" : "Add"} ${label}`}
                checked={present}
                onCheckedChange={(c) =>
                  onChange(c ? emptyValue({ ...spec, optional: false }) : undefined)
                }
                disabled={disabled}
              />
            ) : null}
          </legend>
          {present || !spec.optional ? (
            <SchemaForm
              fields={spec.fields}
              value={obj}
              onChange={onChange}
              issues={issues}
              basePath={path}
              disabled={disabled}
            />
          ) : (
            <p className="text-muted-foreground text-xs">Not set.</p>
          )}
          {error ? <p className="mt-1 text-destructive text-xs">{error}</p> : null}
        </fieldset>
      );
    }
    default:
      return (
        <Wrap id={id} label={label} error={error} hint="Edited as JSON">
          <Textarea
            id={id}
            rows={3}
            className="font-mono text-xs"
            defaultValue={value === undefined ? "" : JSON.stringify(value, null, 2)}
            onBlur={(e) => {
              try {
                onChange(e.target.value.trim() === "" ? undefined : JSON.parse(e.target.value));
              } catch {
                /* keep previous value */
              }
            }}
            disabled={disabled}
          />
        </Wrap>
      );
  }
}

function MediaField({
  id,
  label,
  value,
  onChange,
  error,
  disabled,
}: {
  id: string;
  label: string;
  value: MediaRef | undefined;
  onChange: (v: MediaRef | undefined) => void;
  error?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Wrap id={id} label={label} error={error}>
      <div className="flex items-start gap-3 rounded-lg border p-2">
        {value?.url ? (
          <img src={value.url} alt={value.alt} className="size-16 rounded object-cover" />
        ) : (
          <div className="grid size-16 place-items-center rounded bg-muted text-muted-foreground">
            <ImageIcon className="size-5" />
          </div>
        )}
        <div className="grid min-w-0 flex-1 gap-1.5">
          <div className="truncate font-mono text-muted-foreground text-xs">
            {value ? value.id : "No image"}
          </div>
          {value ? (
            <Input
              aria-label={`${label} alt text`}
              placeholder="Alt text"
              value={value.alt ?? ""}
              onChange={(e) => onChange({ ...value, alt: e.target.value })}
              disabled={disabled}
            />
          ) : null}
          <div className="flex gap-1.5">
            <Button
              type="button"
              variant="outline"
              size="sm"
              id={id}
              onClick={() => setOpen(true)}
              disabled={disabled}
            >
              {value ? "Change" : "Choose image"}
            </Button>
            {value ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onChange(undefined)}
                disabled={disabled}
              >
                <X /> Clear
              </Button>
            ) : null}
          </div>
        </div>
      </div>
      <MediaPickerDialog open={open} onOpenChange={setOpen} onPick={(ref) => onChange(ref)} />
    </Wrap>
  );
}

function ArrayField({
  label,
  spec,
  value,
  onChange,
  path,
  issues,
  disabled,
}: {
  label: string;
  spec: Extract<FieldSpec, { kind: "array" }>;
  value: unknown[];
  onChange: (v: unknown[]) => void;
  path: (string | number)[];
  issues?: readonly Issue[];
  disabled?: boolean;
}) {
  const error = issueFor(issues, path);
  const update = (i: number, v: unknown) => onChange(value.map((x, j) => (j === i ? v : x)));
  const remove = (i: number) => onChange(value.filter((_, j) => j !== i));
  const move = (i: number, d: -1 | 1) => {
    const j = i + d;
    if (j < 0 || j >= value.length) return;
    const next = [...value];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const isObj = spec.element.kind === "object";
  const canAdd = spec.max === undefined || value.length < spec.max;
  return (
    <fieldset className="rounded-lg border p-3">
      <legend className="px-1 font-medium text-sm">
        {label}
        <span className="ml-1 font-normal text-muted-foreground text-xs">
          {value.length}
          {spec.max ? ` / ${spec.max}` : ""}
        </span>
      </legend>
      <div className="grid gap-2">
        {value.map((item, i) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: positional rows
            key={i}
            className={cn("flex gap-2", isObj && "rounded-md border bg-muted/30 p-2")}
          >
            <div className="min-w-0 flex-1">
              {isObj && spec.element.kind === "object" ? (
                <SchemaForm
                  fields={spec.element.fields}
                  value={(item ?? {}) as Record<string, unknown>}
                  onChange={(v) => update(i, v)}
                  issues={issues}
                  basePath={[...path, i]}
                  disabled={disabled}
                />
              ) : (
                <FieldControl
                  label={`${label} ${i + 1}`}
                  spec={spec.element}
                  value={item}
                  onChange={(v) => update(i, v)}
                  path={[...path, i]}
                  issues={issues}
                  disabled={disabled}
                />
              )}
            </div>
            <div className="flex flex-col gap-0.5">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-7"
                aria-label={`Move ${label} ${i + 1} up`}
                onClick={() => move(i, -1)}
                disabled={disabled || i === 0}
              >
                <ArrowUp />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-7"
                aria-label={`Move ${label} ${i + 1} down`}
                onClick={() => move(i, 1)}
                disabled={disabled || i === value.length - 1}
              >
                <ArrowDown />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-7 text-destructive"
                aria-label={`Remove ${label} ${i + 1}`}
                onClick={() => remove(i)}
                disabled={disabled}
              >
                <Trash2 />
              </Button>
            </div>
          </div>
        ))}
        <div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              onChange([...value, emptyValue({ ...spec.element, optional: false }) ?? ""])
            }
            disabled={disabled || !canAdd}
          >
            <Plus /> Add {label.toLowerCase().replace(/s$/, "")}
          </Button>
        </div>
        {error ? <p className="text-destructive text-xs">{error}</p> : null}
      </div>
    </fieldset>
  );
}
